import _defineProperty from "@babel/runtime/helpers/defineProperty";
export let Mode = /*#__PURE__*/function (Mode) {
  Mode[Mode["LEXICAL"] = 0] = "LEXICAL";
  Mode[Mode["STRICT"] = 1] = "STRICT";
  return Mode;
}({});
export const SourceNode = Symbol.for("SourceNode");
export const FormalParameters = Symbol.for("FormalParameters");
export const ECMAScriptCode = Symbol.for("ECMAScriptCode");
export const Environment = Symbol.for("Environment");
export const IsConstructor = Symbol.for("IsConstructor");
export const ThisMode = Symbol.for("ThisMode");
export const DebuggerCall = Symbol.for("$DebuggerCall$");
export const DebuggerScope = Symbol.for("$DebuggerScope$");
export const DebuggerNode = Symbol.for("$DebuggerNode$");
export const DebuggerReturn = Symbol.for("$DebuggerReturn$");

// https://tc39.es/ecma262/#sec-execution-contexts
export class ExecutionContext {
  constructor() {
    _defineProperty(this, "VariableEnvironment", void 0);
    _defineProperty(this, "LexicalEnvironment", void 0);
    _defineProperty(this, "Function", void 0);
  }
}
export let BindingStatus = /*#__PURE__*/function (BindingStatus) {
  BindingStatus[BindingStatus["UNINITIALIZED"] = 0] = "UNINITIALIZED";
  BindingStatus[BindingStatus["LEXICAL"] = 1] = "LEXICAL";
  BindingStatus[BindingStatus["INITIALIZED"] = 2] = "INITIALIZED";
  return BindingStatus;
}({});

// https://tc39.es/ecma262/#sec-environment-records
export class EnvironmentRecord {
  constructor(outer) {
    _defineProperty(this, "OuterEnv", void 0);
    _defineProperty(this, "bindingMap", new Map());
    _defineProperty(this, "ThisValue", undefined);
    _defineProperty(this, "ThisBindingStatus", void 0);
    this.OuterEnv = outer;
  }
  HasBinding(name) {
    return this.bindingMap.has(name);
  }
  CreateMutableBinding(name, deletable) {
    // Assert: binding does not exist.
    this.bindingMap.set(name, {
      mutable: true,
      deletable
    });
    return NormalCompletion(undefined);
  }

  /**
   * Create an immutable binding.
   *
   * @param name - The binding name.
   * @param strict - For named function expressions, strict is false, otherwise it's true.
   * @returns CompletionRecord.
   */
  CreateImmutableBinding(name, strict) {
    // Assert: binding does not exist.
    this.bindingMap.set(name, {
      strict
    });
    return NormalCompletion(undefined);
  }
  InitializeBinding(name, value) {
    const binding = this.bindingMap.get(name);
    // Assert: binding exists and uninitialized.
    Object.assign(binding, {
      initialized: true,
      value
    });
    return NormalCompletion(undefined);
  }

  /**
   * Update a mutable binding value, including function declarations.
   *
   * @param name - The binding name.
   * @param value - The binding value.
   * @param strict - For functions, strict is always false, otherwise it depends on strict-mode.
   * @returns
   */
  SetMutableBinding(name, value, _strict) {
    const binding = this.bindingMap.get(name);
    // Assert: binding exists.
    if (!binding.initialized) {
      throw new ReferenceError(`${name} is not initialized`);
    } else if (binding.mutable) {
      binding.value = value;
    } else {
      throw new TypeError(`Assignment to constant variable`);
    }
    return NormalCompletion(undefined);
  }
  GetBindingValue(name, _strict) {
    const binding = this.bindingMap.get(name);
    // Assert: binding exists.
    if (!binding.initialized) {
      throw new ReferenceError(`${name} is not initialized`);
    }
    return binding.value;
  }
  HasThisBinding() {
    return false;
  }
}
export class DeclarativeEnvironment extends EnvironmentRecord {}
export class FunctionEnvironment extends EnvironmentRecord {
  constructor(F) {
    super(F[Environment]);
    if (F[ThisMode] === Mode.LEXICAL) {
      this.ThisBindingStatus = BindingStatus.LEXICAL;
    } else {
      this.ThisBindingStatus = BindingStatus.UNINITIALIZED;
    }
  }
  HasThisBinding() {
    return this.ThisBindingStatus !== BindingStatus.LEXICAL;
  }
  BindThisValue(value) {
    // Assert: envRec.[[ThisBindingStatus]] is not LEXICAL.
    if (this.ThisBindingStatus === BindingStatus.INITIALIZED) {
      throw new Error("This binding has been initialized");
    }
    this.ThisValue = value;
    this.ThisBindingStatus = BindingStatus.INITIALIZED;
  }
  GetThisBinding() {
    // Assert: envRec.[[ThisBindingStatus]] is not LEXICAL.
    if (this.ThisBindingStatus === BindingStatus.UNINITIALIZED) {
      throw new Error("This binding is not initialized");
    }
    return this.ThisValue;
  }
}
// https://tc39.es/ecma262/#sec-reference-record-specification-type
export class ReferenceRecord {
  constructor(base, referenceName, strict) {
    _defineProperty(this, "Base", void 0);
    _defineProperty(this, "ReferenceName", void 0);
    /** Whether the reference is in strict mode. */
    _defineProperty(this, "Strict", void 0);
    this.Base = base;
    this.ReferenceName = referenceName;
    this.Strict = strict;
  }
}

// https://tc39.es/ecma262/#sec-completion-record-specification-type
export class CompletionRecord {
  constructor(type, value) {
    _defineProperty(this, "Type", void 0);
    _defineProperty(this, "Value", void 0);
    this.Type = type;
    this.Value = value;
  }
}
// https://tc39.es/ecma262/#sec-normalcompletion
export function NormalCompletion(value) {
  return new CompletionRecord("normal", value);
}
export const Empty = Symbol("empty completion");
//# sourceMappingURL=ExecutionContext.js.map